/**
 * List of fallback timezones to use when the user's timezone cannot be determined.
 */
export const FALLBACK_TIMEZONES = [
  // UTC
  "UTC",

  // Africa
  "Africa/Abidjan",
  "Africa/Accra",
  "Africa/Addis_Ababa",
  "Africa/Algiers",
  "Africa/Cairo",
  "Africa/Casablanca",
  "Africa/Johannesburg",
  "Africa/Lagos",
  "Africa/Nairobi",
  "Africa/Tunis",

  // America - North
  "America/Anchorage",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/New_York",
  "America/Phoenix",
  "America/Toronto",
  "America/Vancouver",

  // America - Central
  "America/Costa_Rica",
  "America/Guatemala",
  "America/Mexico_City",
  "America/Panama",

  // America - South
  "America/Argentina/Buenos_Aires",
  "America/Bogota",
  "America/Caracas",
  "America/Lima",
  "America/Santiago",
  "America/Sao_Paulo",

  // America - Caribbean
  "America/Havana",
  "America/Jamaica",
  "America/Puerto_Rico",

  // Antarctica
  "Antarctica/Casey",
  "Antarctica/Davis",
  "Antarctica/McMurdo",

  // Asia - West
  "Asia/Amman",
  "Asia/Baghdad",
  "Asia/Beirut",
  "Asia/Dubai",
  "Asia/Jerusalem",
  "Asia/Kuwait",
  "Asia/Riyadh",
  "Asia/Tehran",

  // Asia - Central
  "Asia/Kabul",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Tashkent",

  // Asia - East
  "Asia/Bangkok",
  "Asia/Hong_Kong",
  "Asia/Jakarta",
  "Asia/Manila",
  "Asia/Shanghai",
  "Asia/Singapore",
  "Asia/Taipei",
  "Asia/Tokyo",

  // Atlantic
  "Atlantic/Azores",
  "Atlantic/Bermuda",
  "Atlantic/Reykjavik",

  // Australia
  "Australia/Adelaide",
  "Australia/Brisbane",
  "Australia/Darwin",
  "Australia/Melbourne",
  "Australia/Perth",
  "Australia/Sydney",

  // Europe - West
  "Europe/Dublin",
  "Europe/Lisbon",
  "Europe/London",

  // Europe - Central
  "Europe/Amsterdam",
  "Europe/Berlin",
  "Europe/Brussels",
  "Europe/Copenhagen",
  "Europe/Madrid",
  "Europe/Paris",
  "Europe/Rome",
  "Europe/Stockholm",
  "Europe/Vienna",
  "Europe/Warsaw",
  "Europe/Zurich",

  // Europe - East
  "Europe/Athens",
  "Europe/Bucharest",
  "Europe/Helsinki",
  "Europe/Istanbul",
  "Europe/Kiev",
  "Europe/Moscow",
  "Europe/Riga",
  "Europe/Sofia",

  // Indian
  "Indian/Maldives",
  "Indian/Mauritius",

  // Pacific
  "Pacific/Auckland",
  "Pacific/Fiji",
  "Pacific/Guam",
  "Pacific/Honolulu",
  "Pacific/Noumea",
  "Pacific/Pago_Pago",
  "Pacific/Tahiti",
] as const;

/**
 * All region keys used for timezone grouping.
 * Translation keys follow the pattern: `timezone.regions.<key>`
 * where key is a camelCase identifier (e.g., "americaNorth").
 */
export const TIMEZONE_REGION_KEYS = [
  "UTC",
  "africa",
  "america",
  "americaNorth",
  "americaCentral",
  "americaSouth",
  "americaCaribbean",
  "antarctica",
  "asiaWest",
  "asiaCentral",
  "asiaEast",
  "atlantic",
  "australia",
  "europe",
  "europeWest",
  "europeCentral",
  "europeEast",
  "indian",
  "pacific",
  "other",
] as const;

/** Maps internal region identifiers to their translation keys. */
export const REGION_TO_KEY: Record<string, string> = {
  UTC: "UTC",
  Africa: "africa",
  America: "america",
  "America - North": "americaNorth",
  "America - Central": "americaCentral",
  "America - South": "americaSouth",
  "America - Caribbean": "americaCaribbean",
  Antarctica: "antarctica",
  "Asia - West": "asiaWest",
  "Asia - Central": "asiaCentral",
  "Asia - East": "asiaEast",
  Asia: "asiaEast",
  Atlantic: "atlantic",
  Australia: "australia",
  Europe: "europe",
  "Europe - West": "europeWest",
  "Europe - Central": "europeCentral",
  "Europe - East": "europeEast",
  Indian: "indian",
  Pacific: "pacific",
  Other: "other",
};

/**
 * Country code (ISO 3166-1 alpha-2) to default timezone mapping.
 * Covers all countries and territories from IANA zone.tab.
 * For countries with multiple timezones, uses the timezone of the capital or biggest airport.
 * This is the single source of truth — the reverse mapping is derived automatically.
 */
export const COUNTRY_TO_TIMEZONE: Record<string, string> = {
  // ── Europe ──────────────────────────────────────────────────────────────
  AD: "Europe/Andorra",
  AL: "Europe/Tirane",
  AT: "Europe/Vienna",
  AX: "Europe/Mariehamn",
  BA: "Europe/Sarajevo",
  BE: "Europe/Brussels",
  BG: "Europe/Sofia",
  BY: "Europe/Minsk",
  CH: "Europe/Zurich",
  CZ: "Europe/Prague",
  DE: "Europe/Berlin",
  DK: "Europe/Copenhagen",
  EE: "Europe/Tallinn",
  ES: "Europe/Madrid",
  FI: "Europe/Helsinki",
  FO: "Atlantic/Faroe",
  FR: "Europe/Paris",
  GB: "Europe/London",
  GG: "Europe/Guernsey",
  GI: "Europe/Gibraltar",
  GR: "Europe/Athens",
  HR: "Europe/Zagreb",
  HU: "Europe/Budapest",
  IE: "Europe/Dublin",
  IM: "Europe/Isle_of_Man",
  IS: "Atlantic/Reykjavik",
  IT: "Europe/Rome",
  JE: "Europe/Jersey",
  LI: "Europe/Vaduz",
  LT: "Europe/Vilnius",
  LU: "Europe/Luxembourg",
  LV: "Europe/Riga",
  MC: "Europe/Monaco",
  MD: "Europe/Chisinau",
  ME: "Europe/Podgorica",
  MK: "Europe/Skopje",
  MT: "Europe/Malta",
  NL: "Europe/Amsterdam",
  NO: "Europe/Oslo",
  PL: "Europe/Warsaw",
  PT: "Europe/Lisbon",
  RO: "Europe/Bucharest",
  RS: "Europe/Belgrade",
  SE: "Europe/Stockholm",
  SI: "Europe/Ljubljana",
  SJ: "Arctic/Longyearbyen",
  SK: "Europe/Bratislava",
  SM: "Europe/San_Marino",
  TR: "Europe/Istanbul",
  UA: "Europe/Kyiv",
  VA: "Europe/Vatican",

  // ── Asia ────────────────────────────────────────────────────────────────
  AE: "Asia/Dubai",
  AF: "Asia/Kabul",
  AM: "Asia/Yerevan",
  AZ: "Asia/Baku",
  BD: "Asia/Dhaka",
  BH: "Asia/Bahrain",
  BN: "Asia/Brunei",
  BT: "Asia/Thimphu",
  CN: "Asia/Shanghai",
  CY: "Asia/Nicosia",
  GE: "Asia/Tbilisi",
  HK: "Asia/Hong_Kong",
  ID: "Asia/Jakarta",
  IL: "Asia/Jerusalem",
  IN: "Asia/Kolkata",
  IQ: "Asia/Baghdad",
  IR: "Asia/Tehran",
  JO: "Asia/Amman",
  JP: "Asia/Tokyo",
  KG: "Asia/Bishkek",
  KH: "Asia/Phnom_Penh",
  KP: "Asia/Pyongyang",
  KR: "Asia/Seoul",
  KW: "Asia/Kuwait",
  KZ: "Asia/Almaty",
  LA: "Asia/Vientiane",
  LB: "Asia/Beirut",
  LK: "Asia/Colombo",
  MM: "Asia/Yangon",
  MN: "Asia/Ulaanbaatar",
  MO: "Asia/Macau",
  MY: "Asia/Kuala_Lumpur",
  NP: "Asia/Kathmandu",
  OM: "Asia/Muscat",
  PH: "Asia/Manila",
  PK: "Asia/Karachi",
  PS: "Asia/Gaza",
  QA: "Asia/Qatar",
  SA: "Asia/Riyadh",
  SG: "Asia/Singapore",
  SY: "Asia/Damascus",
  TH: "Asia/Bangkok",
  TJ: "Asia/Dushanbe",
  TL: "Asia/Dili",
  TM: "Asia/Ashgabat",
  TW: "Asia/Taipei",
  UZ: "Asia/Tashkent",
  VN: "Asia/Ho_Chi_Minh",
  YE: "Asia/Aden",

  // ── Africa ──────────────────────────────────────────────────────────────
  AO: "Africa/Luanda",
  BF: "Africa/Ouagadougou",
  BI: "Africa/Bujumbura",
  BJ: "Africa/Porto-Novo",
  BW: "Africa/Gaborone",
  CD: "Africa/Kinshasa",
  CF: "Africa/Bangui",
  CG: "Africa/Brazzaville",
  CI: "Africa/Abidjan",
  CM: "Africa/Douala",
  DJ: "Africa/Djibouti",
  DZ: "Africa/Algiers",
  EG: "Africa/Cairo",
  EH: "Africa/El_Aaiun",
  ER: "Africa/Asmara",
  ET: "Africa/Addis_Ababa",
  GA: "Africa/Libreville",
  GH: "Africa/Accra",
  GM: "Africa/Banjul",
  GN: "Africa/Conakry",
  GQ: "Africa/Malabo",
  GW: "Africa/Bissau",
  KE: "Africa/Nairobi",
  LR: "Africa/Monrovia",
  LS: "Africa/Maseru",
  LY: "Africa/Tripoli",
  MA: "Africa/Casablanca",
  ML: "Africa/Bamako",
  MR: "Africa/Nouakchott",
  MW: "Africa/Blantyre",
  MZ: "Africa/Maputo",
  NA: "Africa/Windhoek",
  NE: "Africa/Niamey",
  NG: "Africa/Lagos",
  RW: "Africa/Kigali",
  SD: "Africa/Khartoum",
  SL: "Africa/Freetown",
  SN: "Africa/Dakar",
  SO: "Africa/Mogadishu",
  SS: "Africa/Juba",
  ST: "Africa/Sao_Tome",
  SZ: "Africa/Mbabane",
  TD: "Africa/Ndjamena",
  TG: "Africa/Lome",
  TN: "Africa/Tunis",
  TZ: "Africa/Dar_es_Salaam",
  UG: "Africa/Kampala",
  ZA: "Africa/Johannesburg",
  ZM: "Africa/Lusaka",
  ZW: "Africa/Harare",

  // ── Americas — North (multi-timezone) ──────────────────────────────────
  CA: "America/Toronto",
  MX: "America/Mexico_City",
  US: "America/New_York",

  // ── Americas — Central ─────────────────────────────────────────────────
  BZ: "America/Belize",
  CR: "America/Costa_Rica",
  GT: "America/Guatemala",
  HN: "America/Tegucigalpa",
  NI: "America/Managua",
  PA: "America/Panama",
  SV: "America/El_Salvador",

  // ── Americas — Caribbean ───────────────────────────────────────────────
  AG: "America/Antigua",
  AI: "America/Anguilla",
  AW: "America/Aruba",
  BB: "America/Barbados",
  BL: "America/St_Barthelemy",
  BQ: "America/Kralendijk",
  BS: "America/Nassau",
  CU: "America/Havana",
  CW: "America/Curacao",
  DM: "America/Dominica",
  DO: "America/Santo_Domingo",
  GD: "America/Grenada",
  GP: "America/Guadeloupe",
  HT: "America/Port-au-Prince",
  JM: "America/Jamaica",
  KN: "America/St_Kitts",
  KY: "America/Cayman",
  LC: "America/St_Lucia",
  MF: "America/Marigot",
  MQ: "America/Martinique",
  MS: "America/Montserrat",
  PR: "America/Puerto_Rico",
  SX: "America/Lower_Princes",
  TC: "America/Grand_Turk",
  TT: "America/Port_of_Spain",
  VC: "America/St_Vincent",
  VG: "America/Tortola",
  VI: "America/St_Thomas",

  // ── Americas — South ───────────────────────────────────────────────────
  AR: "America/Argentina/Buenos_Aires",
  BO: "America/La_Paz",
  BR: "America/Sao_Paulo",
  CL: "America/Santiago",
  CO: "America/Bogota",
  EC: "America/Guayaquil",
  GF: "America/Cayenne",
  GY: "America/Guyana",
  PE: "America/Lima",
  PY: "America/Asuncion",
  SR: "America/Paramaribo",
  UY: "America/Montevideo",
  VE: "America/Caracas",

  // ── Americas — Other ───────────────────────────────────────────────────
  GL: "America/Nuuk",
  PM: "America/Miquelon",

  // ── Atlantic ────────────────────────────────────────────────────────────
  BM: "Atlantic/Bermuda",
  CV: "Atlantic/Cape_Verde",
  FK: "Atlantic/Stanley",
  GS: "Atlantic/South_Georgia",
  SH: "Atlantic/St_Helena",

  // ── Indian Ocean ────────────────────────────────────────────────────────
  CC: "Indian/Cocos",
  CX: "Indian/Christmas",
  IO: "Indian/Chagos",
  KM: "Indian/Comoro",
  MG: "Indian/Antananarivo",
  MU: "Indian/Mauritius",
  MV: "Indian/Maldives",
  RE: "Indian/Reunion",
  SC: "Indian/Mahe",
  TF: "Indian/Kerguelen",
  YT: "Indian/Mayotte",

  // ── Australia & Oceania ─────────────────────────────────────────────────
  AU: "Australia/Sydney",
  AS: "Pacific/Pago_Pago",
  CK: "Pacific/Rarotonga",
  FJ: "Pacific/Fiji",
  FM: "Pacific/Chuuk",
  GU: "Pacific/Guam",
  KI: "Pacific/Tarawa",
  MH: "Pacific/Majuro",
  MP: "Pacific/Saipan",
  NC: "Pacific/Noumea",
  NF: "Pacific/Norfolk",
  NR: "Pacific/Nauru",
  NU: "Pacific/Niue",
  NZ: "Pacific/Auckland",
  PF: "Pacific/Tahiti",
  PG: "Pacific/Port_Moresby",
  PN: "Pacific/Pitcairn",
  PW: "Pacific/Palau",
  SB: "Pacific/Guadalcanal",
  TK: "Pacific/Fakaofo",
  TO: "Pacific/Tongatapu",
  TV: "Pacific/Funafuti",
  VU: "Pacific/Efate",
  WF: "Pacific/Wallis",
  WS: "Pacific/Apia",

  // ── Multi-timezone — primary ────────────────────────────────────────────
  RU: "Europe/Moscow",

  // ── Antarctica & Minor Territories ──────────────────────────────────────
  AQ: "Antarctica/McMurdo",
  UM: "Pacific/Wake",
};

/**
 * Reverse mapping: timezone ID → country code (ISO 3166-1 alpha-2).
 * Built automatically from COUNTRY_TO_TIMEZONE.
 * For multi-timezone countries, all IANA timezone IDs from zone.tab are mapped.
 * Legacy IANA aliases (e.g., Asia/Calcutta → IN) are included for
 * cross-runtime compatibility.
 */
export const TIMEZONE_TO_COUNTRY_CODE: Record<string, string> = ((): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const [code, tz] of Object.entries(COUNTRY_TO_TIMEZONE)) {
    map[tz] = code;
  }

  // All additional timezone IDs for multi-timezone countries, plus legacy aliases.
  // Source: IANA zone.tab + known legacy↔modern aliases.
  const additionalTimezoneIdsByCountry: Record<string, string[]> = {
    // ── Antarctica (research stations) ──────────────────────────────────
    AQ: [
      "Antarctica/Casey",
      "Antarctica/Davis",
      "Antarctica/DumontDUrville",
      "Antarctica/Mawson",
      "Antarctica/McMurdo",
      "Antarctica/Palmer",
      "Antarctica/Rothera",
      "Antarctica/Syowa",
      "Antarctica/Troll",
      "Antarctica/Vostok",
    ],

    // ── Argentina ───────────────────────────────────────────────────────
    AR: [
      "America/Argentina/Buenos_Aires",
      "America/Argentina/Catamarca",
      "America/Argentina/Cordoba",
      "America/Argentina/Jujuy",
      "America/Argentina/La_Rioja",
      "America/Argentina/Mendoza",
      "America/Argentina/Rio_Gallegos",
      "America/Argentina/Salta",
      "America/Argentina/San_Juan",
      "America/Argentina/San_Luis",
      "America/Argentina/Tucuman",
      "America/Argentina/Ushuaia",
    ],

    // ── Australia ───────────────────────────────────────────────────────
    AU: [
      "Antarctica/Macquarie",
      "Australia/Adelaide",
      "Australia/Brisbane",
      "Australia/Broken_Hill",
      "Australia/Darwin",
      "Australia/Eucla",
      "Australia/Hobart",
      "Australia/Lindeman",
      "Australia/Lord_Howe",
      "Australia/Melbourne",
      "Australia/Perth",
      "Australia/Sydney",
    ],

    // ── Brazil ──────────────────────────────────────────────────────────
    BR: [
      "America/Araguaina",
      "America/Bahia",
      "America/Belem",
      "America/Boa_Vista",
      "America/Campo_Grande",
      "America/Cuiaba",
      "America/Eirunepe",
      "America/Fortaleza",
      "America/Maceio",
      "America/Manaus",
      "America/Noronha",
      "America/Porto_Velho",
      "America/Recife",
      "America/Rio_Branco",
      "America/Santarem",
      "America/Sao_Paulo",
    ],

    // ── Canada ──────────────────────────────────────────────────────────
    CA: [
      "America/Atikokan",
      "America/Blanc-Sablon",
      "America/Cambridge_Bay",
      "America/Creston",
      "America/Dawson",
      "America/Dawson_Creek",
      "America/Edmonton",
      "America/Fort_Nelson",
      "America/Glace_Bay",
      "America/Goose_Bay",
      "America/Halifax",
      "America/Inuvik",
      "America/Iqaluit",
      "America/Moncton",
      "America/Pangnirtung",
      "America/Rankin_Inlet",
      "America/Regina",
      "America/Resolute",
      "America/St_Johns",
      "America/Swift_Current",
      "America/Toronto",
      "America/Vancouver",
      "America/Whitehorse",
      "America/Winnipeg",
      "America/Yellowknife",
    ],

    // ── Chile ───────────────────────────────────────────────────────────
    CL: ["America/Punta_Arenas", "America/Santiago", "Pacific/Easter"],

    // ── China ───────────────────────────────────────────────────────────
    CN: ["Asia/Shanghai", "Asia/Urumqi"],

    // ── Congo (DR) ──────────────────────────────────────────────────────
    CD: ["Africa/Kinshasa", "Africa/Lubumbashi"],

    // ── Cyprus ──────────────────────────────────────────────────────────
    CY: ["Asia/Famagusta", "Asia/Nicosia"],

    // ── Ecuador ─────────────────────────────────────────────────────────
    EC: ["America/Guayaquil", "Pacific/Galapagos"],

    // ── Spain ───────────────────────────────────────────────────────────
    ES: ["Africa/Ceuta", "Atlantic/Canary", "Europe/Madrid"],

    // ── Micronesia ──────────────────────────────────────────────────────
    FM: ["Pacific/Chuuk", "Pacific/Kosrae", "Pacific/Pohnpei"],

    // ── Germany ─────────────────────────────────────────────────────────
    DE: ["Europe/Berlin", "Europe/Busingen"],

    // ── Greenland ───────────────────────────────────────────────────────
    GL: ["America/Danmarkshavn", "America/Nuuk", "America/Scoresbysund", "America/Thule"],

    // ── Indonesia ───────────────────────────────────────────────────────
    ID: ["Asia/Jakarta", "Asia/Jayapura", "Asia/Makassar", "Asia/Pontianak"],

    // ── Kazakhstan ──────────────────────────────────────────────────────
    KZ: ["Asia/Almaty", "Asia/Aqtau", "Asia/Aqtobe", "Asia/Atyrau", "Asia/Oral", "Asia/Qostanay", "Asia/Qyzylorda"],

    // ── Kiribati ────────────────────────────────────────────────────────
    KI: ["Pacific/Kanton", "Pacific/Kiritimati", "Pacific/Tarawa"],

    // ── Malaysia ────────────────────────────────────────────────────────
    MY: ["Asia/Kuala_Lumpur", "Asia/Kuching"],

    // ── Marshall Islands ────────────────────────────────────────────────
    MH: ["Pacific/Kwajalein", "Pacific/Majuro"],

    // ── Mexico ──────────────────────────────────────────────────────────
    MX: [
      "America/Bahia_Banderas",
      "America/Cancun",
      "America/Chihuahua",
      "America/Ciudad_Juarez",
      "America/Hermosillo",
      "America/Matamoros",
      "America/Mazatlan",
      "America/Merida",
      "America/Mexico_City",
      "America/Monterrey",
      "America/Ojinaga",
      "America/Tijuana",
    ],

    // ── Mongolia ────────────────────────────────────────────────────────
    MN: ["Asia/Choibalsan", "Asia/Hovd", "Asia/Ulaanbaatar"],

    // ── New Zealand ─────────────────────────────────────────────────────
    NZ: ["Pacific/Auckland", "Pacific/Chatham"],

    // ── Palestine ───────────────────────────────────────────────────────
    PS: ["Asia/Gaza", "Asia/Hebron"],

    // ── French Polynesia ────────────────────────────────────────────────
    PF: ["Pacific/Gambier", "Pacific/Marquesas", "Pacific/Tahiti"],

    // ── Papua New Guinea ────────────────────────────────────────────────
    PG: ["Pacific/Bougainville", "Pacific/Port_Moresby"],

    // ── Portugal ────────────────────────────────────────────────────────
    PT: ["Atlantic/Azores", "Atlantic/Madeira", "Europe/Lisbon"],

    // ── Russia ──────────────────────────────────────────────────────────
    RU: [
      "Asia/Anadyr",
      "Asia/Barnaul",
      "Asia/Chita",
      "Asia/Irkutsk",
      "Asia/Kamchatka",
      "Asia/Khandyga",
      "Asia/Krasnoyarsk",
      "Asia/Magadan",
      "Asia/Novokuznetsk",
      "Asia/Novosibirsk",
      "Asia/Omsk",
      "Asia/Sakhalin",
      "Asia/Srednekolymsk",
      "Asia/Tomsk",
      "Asia/Ust-Nera",
      "Asia/Vladivostok",
      "Asia/Yakutsk",
      "Asia/Yekaterinburg",
      "Europe/Astrakhan",
      "Europe/Kaliningrad",
      "Europe/Kirov",
      "Europe/Moscow",
      "Europe/Samara",
      "Europe/Saratov",
      "Europe/Ulyanovsk",
      "Europe/Volgograd",
    ],

    // ── Ukraine ─────────────────────────────────────────────────────────
    UA: ["Europe/Kiev", "Europe/Kyiv", "Europe/Simferopol"],

    // ── United States ───────────────────────────────────────────────────
    US: [
      "America/Adak",
      "America/Anchorage",
      "America/Boise",
      "America/Chicago",
      "America/Denver",
      "America/Detroit",
      "America/Indiana/Indianapolis",
      "America/Indiana/Knox",
      "America/Indiana/Marengo",
      "America/Indiana/Petersburg",
      "America/Indiana/Tell_City",
      "America/Indiana/Vevay",
      "America/Indiana/Vincennes",
      "America/Indiana/Winamac",
      "America/Juneau",
      "America/Kentucky/Louisville",
      "America/Kentucky/Monticello",
      "America/Los_Angeles",
      "America/Menominee",
      "America/Metlakatla",
      "America/New_York",
      "America/Nome",
      "America/North_Dakota/Beulah",
      "America/North_Dakota/Center",
      "America/North_Dakota/New_Salem",
      "America/Phoenix",
      "America/Sitka",
      "America/Yakutat",
      "Pacific/Honolulu",
    ],

    // ── US Minor Outlying Islands ───────────────────────────────────────
    UM: ["Pacific/Midway", "Pacific/Wake"],

    // ── Uzbekistan ──────────────────────────────────────────────────────
    UZ: ["Asia/Samarkand", "Asia/Tashkent"],

    // ── Legacy IANA aliases (cross-runtime compatibility) ───────────────
    // Different JS runtimes may return either modern or legacy names.
    IN: ["Asia/Calcutta", "Asia/Kolkata"],
    VN: ["Asia/Ho_Chi_Minh", "Asia/Saigon"],
    MM: ["Asia/Rangoon", "Asia/Yangon"],
    TR: ["Asia/Istanbul", "Europe/Istanbul"],
  };

  for (const [countryCode, timezoneIds] of Object.entries(additionalTimezoneIdsByCountry)) {
    for (const timezoneId of timezoneIds) {
      map[timezoneId] = countryCode;
    }
  }

  return map;
})();
